import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  RefreshCw, Search, X, MapPin, Route, Coffee, Clock, Briefcase,
  Calendar, Loader2, CheckSquare, AlertTriangle, MessageSquare,
  ChevronDown, Bell,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_MS = 7_000;

const STATUS_COLOR = {
  on_visit:       { bg: 'bg-blue-100',    text: 'text-blue-800',    dot: 'bg-blue-500',    label: 'On Visit' },
  on_break:       { bg: 'bg-orange-100',  text: 'text-orange-800',  dot: 'bg-orange-500',  label: 'On Break' },
  on_permission:  { bg: 'bg-purple-100',  text: 'text-purple-800',  dot: 'bg-purple-500',  label: 'Permission' },
  in_office:      { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500', label: 'In Office' },
  checked_out:    { bg: 'bg-gray-100',    text: 'text-gray-700',    dot: 'bg-gray-400',    label: 'Checked Out' },
  not_checked_in: { bg: 'bg-rose-50',     text: 'text-rose-700',    dot: 'bg-rose-400',    label: 'No Check-in' },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};
const fmtMinutes = (m) => {
  if (m == null) return '—';
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};

export default function DailyReport() {
  const today = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today());
  const [staffPicker, setStaffPicker] = useState('');         // search box
  const [staffOptions, setStaffOptions] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);   // {id, name, mobile, ...}
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [mapModal, setMapModal] = useState(null);

  // Debounced staff lookup as the user types.
  const debTimer = useRef(null);
  useEffect(() => {
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(async () => {
      if (!pickerOpen) return;
      try {
        const token = localStorage.getItem('token');
        const url = `${API_URL}/api/admin/staff-options${staffPicker.trim() ? `?q=${encodeURIComponent(staffPicker.trim())}` : ''}`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        setStaffOptions(d.staff || []);
      } catch (_) {}
    }, 250);
    return () => debTimer.current && clearTimeout(debTimer.current);
  }, [staffPicker, pickerOpen]);

  const loadReport = useCallback(async () => {
    if (!selectedStaff?.id) return;
    setLoading(true); setErr('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/daily-report?staff_id=${selectedStaff.id}&date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status} ${t.slice(0, 120)}`);
      }
      const d = await r.json();
      setReport(d);
    } catch (e) {
      setErr(`Could not load report: ${e.message || e}`);
      setReport(null);
    } finally { setLoading(false); }
  }, [selectedStaff?.id, date]);

  // Auto-load whenever staff or date changes.
  useEffect(() => {
    if (selectedStaff?.id) loadReport();
    else setReport(null);
  }, [selectedStaff?.id, date, loadReport]);

  // Optional polling for live status (every 30s).
  useEffect(() => {
    if (!autoRefresh || !selectedStaff?.id) return;
    const i = setInterval(loadReport, AUTO_REFRESH_MS);
    return () => clearInterval(i);
  }, [autoRefresh, selectedStaff?.id, loadReport]);

  const stat = report ? (STATUS_COLOR[report.current_status] || STATUS_COLOR.not_checked_in) : null;

  return (
    <div className="space-y-4" data-testid="daily-report-page">
      <div className="flex flex-wrap items-center gap-3">
        <Briefcase className="text-indigo-600" size={22} />
        <h1 className="text-xl font-bold text-gray-900">Daily Report — Staff-wise</h1>
        <span className="text-xs text-gray-500">Pick a staff + date to see the day's full mobile-app mirror</span>
      </div>

      {/* Filter strip */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap items-end gap-3" data-testid="daily-report-filters">
        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span className="font-medium text-gray-700">Date</span>
          <div className="relative">
            <Calendar size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="pl-7 pr-2 py-1.5 border border-gray-200 rounded text-sm w-44"
              data-testid="daily-report-date"
            />
          </div>
        </label>

        <div className="text-xs text-gray-600 flex flex-col gap-1 relative">
          <span className="font-medium text-gray-700">Staff</span>
          <div className="relative w-80 max-w-full">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={selectedStaff ? `${selectedStaff.name}${selectedStaff.staff_code ? ' · ' + selectedStaff.staff_code : ''}` : staffPicker}
              onChange={(e) => {
                if (selectedStaff) { setSelectedStaff(null); setStaffPicker(e.target.value); }
                else setStaffPicker(e.target.value);
                setPickerOpen(true);
              }}
              onFocus={() => { setPickerOpen(true); setStaffOptions([]); }}
              onBlur={() => setTimeout(() => setPickerOpen(false), 200)}
              placeholder="Search name / mobile / staff ID…"
              className="w-full pl-7 pr-7 py-1.5 border border-gray-200 rounded text-sm"
              data-testid="daily-report-staff-search"
            />
            {selectedStaff && (
              <button
                onClick={() => { setSelectedStaff(null); setStaffPicker(''); setReport(null); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                data-testid="daily-report-staff-clear"
              >
                <X size={12} />
              </button>
            )}
            {pickerOpen && (
              <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto z-30">
                {staffOptions.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {staffPicker ? 'No matches.' : 'Type 2+ characters to search…'}
                  </div>
                )}
                {staffOptions.map(s => (
                  <button
                    key={s.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setSelectedStaff(s); setPickerOpen(false); setStaffPicker(''); }}
                    className="block w-full px-3 py-2 text-left hover:bg-indigo-50 text-sm"
                    data-testid={`daily-report-staff-option-${s.id}`}
                  >
                    <div className="font-medium text-gray-900">{s.name}</div>
                    <div className="text-[11px] text-gray-500">
                      {s.mobile}{s.staff_code ? ` · ${s.staff_code}` : ''}{s.department ? ` · ${s.department}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <label className="text-xs text-gray-600 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            disabled={!selectedStaff}
            data-testid="daily-report-autorefresh"
          />
          Auto refresh (7s)
        </label>

        <button
          onClick={loadReport}
          disabled={!selectedStaff || loading}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-lg"
          data-testid="daily-report-refresh"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {loading ? 'Loading…' : 'Reload'}
        </button>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="daily-report-error">{err}</div>
      )}

      {!selectedStaff && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-8 text-center text-indigo-700" data-testid="daily-report-empty">
          <Briefcase className="mx-auto mb-2 opacity-60" size={28} />
          <p className="font-medium">Pick a staff to load the day's report.</p>
          <p className="text-xs text-indigo-600 mt-1">Auto-load is off so the page is instant — no full-org scan.</p>
        </div>
      )}

      {selectedStaff && report && (
        <DetailPanel report={report} stat={stat} onMap={(m) => setMapModal(m)} />
      )}

      {mapModal && <MapModal info={mapModal} onClose={() => setMapModal(null)} />}
    </div>
  );
}

function DetailPanel({ report, stat, onMap }) {
  const s = report.staff;
  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid="daily-report-header">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">{s.name}</h2>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${stat.bg} ${stat.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${stat.dot} ${report.current_status.startsWith('on_') ? 'animate-pulse' : ''}`} />
              {stat.label}
            </span>
            <BatteryOptBadge bo={s.battery_opt} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {s.mobile}
            {s.staff_code && ` · ${s.staff_code}`}
            {s.department && ` · ${s.department}`}
            {s.designation && ` · ${s.designation}`}
            {s.geo_fence_name && ` · ${s.geo_fence_name}`}
          </p>
        </div>
        <div className="flex gap-2">
          {report.current_location && (
            <button
              onClick={() => onMap({ mode: 'live', staffId: s.id, name: s.name, location: report.current_location })}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs"
              data-testid="daily-report-live-map"
            >
              <MapPin size={12} /> Live location
            </button>
          )}
          {report.visits.some(v => v.status === 'ongoing') && (
            <button
              onClick={() => {
                const v = report.visits.find(x => x.status === 'ongoing');
                onMap({ mode: 'route', staffId: s.id, name: s.name, visitId: v.id, purpose: v.purpose });
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs"
              data-testid="daily-report-route-map"
            >
              <Route size={12} /> Live route
            </button>
          )}
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Work" value={fmtMinutes(report.totals.work_minutes)} icon={<Clock size={14} />} tone="emerald" />
        <Stat label="Breaks" value={`${report.breaks.length} (${fmtMinutes(report.totals.break_minutes)})`} icon={<Coffee size={14} />} tone="orange" />
        <Stat label="Visits" value={report.totals.visits_count} icon={<Route size={14} />} tone="blue" />
        <Stat label="Permissions" value={report.totals.permissions_count} icon={<Clock size={14} />} tone="purple" />
        <Stat label="To-Do (pending)" value={`${report.totals.todos_pending} (${report.totals.todos_admin_assigned} admin)`} icon={<CheckSquare size={14} />} tone="indigo" />
        <Stat label="Missing log" value={`${(report.missing_log?.gaps?.length || 0) + (report.missing_log?.exits?.length || 0)}`} icon={<AlertTriangle size={14} />} tone="rose" />
      </div>

      {/* Sections grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Attendance — Check-ins" icon={<Clock size={16} />} count={report.checkins.length}>
          {report.checkins.length === 0 && <Empty msg="No check-ins for the day." />}
          {report.checkins.map((c, i) => (
            <Row key={`ci-${c.in_time || ''}-${c.out_time || ''}-${i}`} tone="emerald">
              <span className="font-mono">{fmtTime(c.in_time)} → {c.out_time ? fmtTime(c.out_time) : <em className="text-emerald-700 font-semibold">ongoing</em>}</span>
              {c.in_inside_geofence === false && <span className="text-amber-700 text-[10px]">outside office</span>}
              {c.in_lat && (
                <a className="ml-auto text-emerald-700 hover:text-emerald-900"
                   href={`https://www.google.com/maps?q=${c.in_lat},${c.in_lng}`} target="_blank" rel="noreferrer"
                   title="Open check-in location"><MapPin size={11} /></a>
              )}
            </Row>
          ))}
        </SectionCard>

        <SectionCard title="Breaks" icon={<Coffee size={16} />} count={report.breaks.length}>
          {report.breaks.length === 0 && <Empty msg="No breaks today." />}
          {report.breaks.map(b => (
            <Row key={b.id} tone={b.status === 'ongoing' ? 'orange-live' : (b.status === 'auto_closed' ? 'gray' : 'orange')}>
              <span className="capitalize font-medium">{b.type.replace(/_/g, ' ')}</span>
              <span className="text-[11px] text-gray-600">{fmtTime(b.start)} → {b.status === 'ongoing' ? <em className="text-orange-700 font-semibold">live</em> : fmtTime(b.end)}</span>
              <span className="ml-auto text-[10px] text-gray-600">
                {fmtMinutes(b.duration_minutes)}{b.allowed_minutes ? `/${b.allowed_minutes}m` : ''}
              </span>
              {b.status === 'auto_closed' && (
                <span className="w-full text-[10px] text-amber-700">⚠ auto-closed: {b.auto_closed_reason}</span>
              )}
            </Row>
          ))}
        </SectionCard>

        <SectionCard title="Visits" icon={<Route size={16} />} count={report.visits.length}>
          {report.visits.length === 0 && <Empty msg="No visits today." />}
          {report.visits.map(v => (
            <Row key={v.id} tone={v.status === 'ongoing' ? 'blue-live' : 'blue'}>
              <span className="font-medium truncate max-w-[180px]">{v.purpose || '—'}</span>
              <span className="text-[11px] text-gray-600">{fmtTime(v.start)} → {v.status === 'ongoing' ? <em className="text-blue-700 font-semibold">live</em> : fmtTime(v.end)}</span>
              {v.suppliers?.length > 0 && (
                <span className="text-[10px] text-gray-500 truncate max-w-[200px]" title={v.suppliers.join(', ')}>· {v.suppliers.join(', ')}</span>
              )}
              <span className="ml-auto text-[10px] text-gray-700">
                {v.google_distance_km != null ? `${Number(v.google_distance_km).toFixed(1)}km` : '—'}
                {v.distance_km != null && v.distance_km !== v.google_distance_km && ` (manual ${v.distance_km})`}
              </span>
              <button onClick={() => onMap({ mode: 'route', staffId: report.staff.id, name: report.staff.name, visitId: v.id, purpose: v.purpose })}
                      className="text-blue-700 hover:text-blue-900" title="Show route">
                <MapPin size={11} />
              </button>
            </Row>
          ))}
        </SectionCard>

        <SectionCard title="Permissions" icon={<Clock size={16} />} count={report.permissions.length}>
          {report.permissions.length === 0 && <Empty msg="No permissions today." />}
          {report.permissions.map(p => (
            <Row key={p.id} tone="purple">
              <span className="font-mono">{fmtTime(p.start)} → {p.status === 'ongoing' ? <em className="text-purple-700 font-semibold">live</em> : fmtTime(p.end)}</span>
              {p.reason && <span className="text-[11px] text-gray-600 truncate max-w-[220px]">· {p.reason}</span>}
              {p.approval_status && <span className="ml-auto text-[10px] uppercase font-semibold text-purple-700">{p.approval_status}</span>}
            </Row>
          ))}
        </SectionCard>

        <SectionCard title="Missing Log" icon={<AlertTriangle size={16} />} count={(report.missing_log?.gaps?.length || 0) + (report.missing_log?.exits?.length || 0)}>
          {(report.missing_log?.gaps?.length || 0) + (report.missing_log?.exits?.length || 0) === 0 && <Empty msg="No GPS gaps or out-of-office runs." />}
          {(report.missing_log?.gaps || []).map((g, i) => (
            <Row key={`g${i}`} tone="rose">
              <span className="text-[11px]">GPS gap</span>
              <span className="font-mono">{fmtTime(g.from)} → {fmtTime(g.to)}</span>
              <span className="ml-auto text-[10px]">{fmtMinutes(Math.round(g.duration_minutes))}</span>
            </Row>
          ))}
          {(report.missing_log?.exits || []).map((e, i) => (
            <Row key={`e${i}`} tone="rose">
              <span className="text-[11px]">Out of office</span>
              <span className="font-mono">{fmtTime(e.from)} → {fmtTime(e.to)}</span>
              <span className="ml-auto text-[10px]">{fmtMinutes(Math.round(e.duration_minutes))}</span>
            </Row>
          ))}
        </SectionCard>

        <SectionCard title="To-Do List" icon={<CheckSquare size={16} />} count={report.todos.length}>
          {report.todos.length === 0 && <Empty msg="No todos." />}
          {report.todos.map(t => (
            <Row key={t.id} tone={t.status === 'pending' ? 'indigo' : 'gray'}>
              <span className="font-medium truncate max-w-[260px]">{t.title}</span>
              <span className="text-[10px] uppercase text-gray-600">{t.status}</span>
              {t.priority && <span className="text-[10px] text-amber-700">{t.priority}</span>}
              {t.created_by_role === 'admin' && <span className="text-[10px] text-indigo-700">admin</span>}
              {t.due_at && <span className="ml-auto text-[10px] text-gray-600">due {new Date(t.due_at).toLocaleDateString()}</span>}
            </Row>
          ))}
        </SectionCard>

        {report.leaves?.length > 0 && (
          <SectionCard title="Leave covering today" icon={<Calendar size={16} />} count={report.leaves.length}>
            {report.leaves.map(l => (
              <Row key={l.id} tone="purple">
                <span className="font-medium capitalize">{l.type}</span>
                <span className="text-[11px]">{l.from_date} → {l.to_date}</span>
                {l.reason && <span className="text-[10px] text-gray-600 truncate max-w-[220px]">{l.reason}</span>}
                {l.status && <span className="ml-auto text-[10px] uppercase text-purple-700">{l.status}</span>}
              </Row>
            ))}
          </SectionCard>
        )}

        <SectionCard title="Notifications sent (last 10)" icon={<Bell size={16} />} count={report.notifications.length}>
          {report.notifications.length === 0 && <Empty msg="No WhatsApp notifications today." />}
          {report.notifications.map((n, i) => (
            <Row key={`notif-${n.sent_at || ''}-${n.template || ''}-${i}`} tone="gray">
              <span className="font-mono text-[11px]">{n.template}</span>
              <span className="text-[10px] text-gray-500">{fmtTime(n.sent_at)}</span>
              <span className="ml-auto text-[10px] uppercase">{n.status}</span>
            </Row>
          ))}
        </SectionCard>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, tone }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    orange:  'bg-orange-50 text-orange-900 border-orange-200',
    blue:    'bg-blue-50 text-blue-900 border-blue-200',
    purple:  'bg-purple-50 text-purple-900 border-purple-200',
    indigo:  'bg-indigo-50 text-indigo-900 border-indigo-200',
    rose:    'bg-rose-50 text-rose-900 border-rose-200',
  };
  return (
    <div className={`border rounded-lg p-2.5 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-70">{icon}{label}</div>
      <div className="text-base font-bold mt-0.5">{value}</div>
    </div>
  );
}

function SectionCard({ title, icon, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
      >
        <span className="text-gray-500">{icon}</span>
        <span className="font-semibold text-sm text-gray-800">{title}</span>
        <span className="text-[11px] text-gray-500">({count})</span>
        <ChevronDown size={14} className={`ml-auto transition-transform ${open ? '' : '-rotate-90'} text-gray-400`} />
      </button>
      {open && <div className="p-2 space-y-1.5">{children}</div>}
    </div>
  );
}

function Row({ children, tone = 'gray' }) {
  const tones = {
    'emerald':     'bg-emerald-50 border-emerald-200 text-emerald-900',
    'orange':      'bg-orange-50 border-orange-200 text-orange-900',
    'orange-live': 'bg-orange-100 border-orange-300 text-orange-900 ring-1 ring-orange-300/60 animate-pulse',
    'blue':        'bg-blue-50 border-blue-200 text-blue-900',
    'blue-live':   'bg-blue-100 border-blue-300 text-blue-900 ring-1 ring-blue-300/60 animate-pulse',
    'purple':      'bg-purple-50 border-purple-200 text-purple-900',
    'rose':        'bg-rose-50 border-rose-200 text-rose-900',
    'indigo':      'bg-indigo-50 border-indigo-200 text-indigo-900',
    'gray':        'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className={`flex items-center flex-wrap gap-2 px-2 py-1.5 border rounded-md text-xs ${tones[tone]}`}>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <p className="text-xs text-gray-500 px-1">{msg}</p>;
}

function MapModal({ info, onClose }) {
  const isRoute = info.mode === 'route';
  const title = isRoute ? `Visit Route — ${info.name}` : `Current Location — ${info.name}`;
  const mapSrc = isRoute
    ? null
    : `https://www.google.com/maps?q=${info.location?.lat},${info.location?.lng}&z=16&output=embed`;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="daily-report-map-modal">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {!isRoute && info.location?.timestamp && (
              <p className="text-xs text-gray-500">Last seen {fmtTime(info.location.timestamp)}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded"><X size={16} /></button>
        </div>
        {isRoute ? <RouteIframe visitId={info.visitId} /> : (
          <iframe title="Current location" src={mapSrc} className="w-full h-[480px] border-0" loading="lazy" />
        )}
      </div>
    </div>
  );
}

function RouteIframe({ visitId }) {
  const [pts, setPts] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/visits/${visitId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const snap = (d.snapped_path && d.snapped_path.length >= 2) ? d.snapped_path :
          (d.gps_path || []).map(g => ({ lat: g.latitude, lng: g.longitude }));
        setPts(snap);
      } catch (e) { if (!cancelled) setErr(e.message || String(e)); }
    })();
    return () => { cancelled = true; };
  }, [visitId]);

  if (err) return <div className="p-8 text-center text-sm text-red-600">{err}</div>;
  // Cost-optimisation (Jun 9, 2026 user ask): the previous renderer used
  // `maps/embed/v1/directions` which makes Google compute & render a road
  // route between origin → waypoints → destination on every page open.
  // Each load is a billable Directions API request. Daily Report visits
  // are opened often by the admin, so the spend added up fast.
  //
  // We now embed only the destination pin via the FREE `maps/embed/v1/view`
  // mode — no route calculation, no Directions billing. Admins still see
  // *where* the visit happened. The "Open in Google Maps" link on the
  // visit card opens consumer Maps in a new tab (no billing) for staff
  // who need the actual route.
  if (!pts) return <div className="p-8 text-center text-sm text-gray-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading route…</div>;
  if (pts.length < 2) return <div className="p-8 text-center text-sm text-gray-500">No GPS pings recorded yet.</div>;

  const last = pts[pts.length - 1];
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const src = `https://www.google.com/maps/embed/v1/view?key=${key}&center=${last.lat},${last.lng}&zoom=14`;
  return (
    <div className="relative w-full h-[480px] border-0">
      <iframe title="Location" src={src} className="w-full h-full border-0" loading="lazy" />
      <a
        href={`https://www.google.com/maps/dir/?api=1&origin=${pts[0].lat},${pts[0].lng}&destination=${last.lat},${last.lng}&travelmode=driving`}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-2 right-2 bg-white/95 hover:bg-white border border-gray-300 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-lg shadow-sm"
        data-testid="open-in-google-maps"
      >
        Open route in Google Maps ↗
      </a>
    </div>
  );
}

/**
 * Shows the staff's Android battery-optimisation state. Helps admins
 * triage GPS-gap complaints — if Battery Saver is still ON on a staff's
 * device, the OS is killing background tracking and no fix on the
 * server will help until they whitelist the app.
 */
function BatteryOptBadge({ bo }) {
  if (!bo) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500"
        title="Staff hasn't run the app yet, or is on web — battery status unknown."
        data-testid="daily-report-battery-unknown"
      >🔋 Battery ?</span>
    );
  }
  if (bo.ignored) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700"
        title={`Battery optimisation OFF · ${bo.manufacturer || ''} ${bo.model || ''} · reported ${fmtTime(bo.reported_at)}`}
        data-testid="daily-report-battery-ok"
      >🛡️ Battery OK</span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 animate-pulse"
      title={`Battery optimisation ON — phone may kill GPS in background · ${bo.manufacturer || ''} ${bo.model || ''} · reported ${fmtTime(bo.reported_at)}`}
      data-testid="daily-report-battery-bad"
    >⚠️ Battery saver ON</span>
  );
}
